import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';

function AIRunbookGenerator() {
  const navigate = useNavigate();
  const [incidentDescription, setIncidentDescription] = useState('');
  const [generatedRunbook, setGeneratedRunbook] = useState(null);
  const [similarRunbooks, setSimilarRunbooks] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (!incidentDescription.trim()) {
      setErrorMessage("Please enter an incident description.");
      return;
    }

    setIsGenerating(true);
    setErrorMessage('');
    setGeneratedRunbook(null);
    setSimilarRunbooks([]);
    
    try {
      // Call both APIs in parallel
      const [generateResponse, identifyResponse] = await Promise.all([
        apiCall("/generate-runbook", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            incident_description: incidentDescription,
          }),
        }),
        apiCall("/identify-runbook", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            incident_description: incidentDescription,
          }),
        })
      ]);

      if (generateResponse.error) {
        setErrorMessage(generateResponse.error);
      } else {
        // Store the generated runbook (without saving to DB yet)
        let runbookData = generateResponse.runbook || generateResponse;
        
        // Handle if runbook is a JSON string
        if (typeof runbookData === 'string') {
          try {
            runbookData = JSON.parse(runbookData);
          } catch (e) {
            console.error("Failed to parse runbook JSON:", e);
          }
        }
        
        setGeneratedRunbook(runbookData);
      }

      if (identifyResponse.error) {
        console.error("Error identifying similar runbooks:", identifyResponse.error);
      } else {
        setSimilarRunbooks(identifyResponse.runbooks || []);
      }
    } catch (error) {
      console.error("AI Runbook Generation Error:", error);
      setErrorMessage("An error occurred while generating the runbook. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generatedRunbook) {
      setErrorMessage("No runbook to save.");
      return;
    }

    setIsSaving(true);
    setErrorMessage('');
    
    try {
      // Call the API with save=true flag
      const response = await apiCall("/generate-runbook", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          incident_description: incidentDescription,
          save: true, // Flag to save to DB
        }),
      });

      if (response.error) {
        setErrorMessage(response.error);
      } else {
        alert("Runbook saved successfully!");
        // Navigate to runbook list or view the saved runbook
        if (response.runbook && (response.runbook['Runbook ID'] || response.runbook.runbook_id)) {
          const runbookId = response.runbook['Runbook ID'] || response.runbook.runbook_id;
          // Navigate to the view page
          navigate(`/view_runbook/${runbookId}`);
        } else {
          navigate("/runbook_list");
        }
      }
    } catch (error) {
      console.error("Save Runbook Error:", error);
      setErrorMessage("An error occurred while saving the runbook.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset all states
    setIncidentDescription('');
    setGeneratedRunbook(null);
    setSimilarRunbooks([]);
    setErrorMessage('');
    // Optionally navigate back
    navigate(-1);
  };

  const handleReset = () => {
    setGeneratedRunbook(null);
    setSimilarRunbooks([]);
    setErrorMessage('');
  };

  return (
    <div className="container mt-4">
      <h1 className="mb-4">AI Runbook Generator</h1>

      {/* Input Form */}
      <form onSubmit={handleGenerate} className="card p-4 mb-4">
        <div className="mb-3">
          <label htmlFor="incidentDescription" className="form-label">
            Incident Description (Prompt):
          </label>
          <textarea
            id="incidentDescription"
            className="form-control"
            rows="6"
            value={incidentDescription}
            onChange={(e) => setIncidentDescription(e.target.value)}
            placeholder="Enter a detailed description of the incident..."
            required
            disabled={isGenerating}
          />
        </div>
        
        {errorMessage && (
          <div className="alert alert-danger" role="alert">
            {errorMessage}
          </div>
        )}

        <div className="d-flex gap-2">
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={isGenerating || !incidentDescription.trim()}
          >
            {isGenerating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Generating...
              </>
            ) : (
              <>
                <i className="bi bi-magic me-2"></i>
                Generate Runbook
              </>
            )}
          </button>
          
          {generatedRunbook && (
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={handleReset}
              disabled={isGenerating}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Reset
            </button>
          )}
        </div>
      </form>

      {/* Generated Runbook Display */}
      {generatedRunbook && (
        <div className="card p-4 mb-4">
          <h2 className="mb-3">
            <i className="bi bi-file-earmark-text me-2"></i>
            Generated Runbook Preview
          </h2>
          
          <div className="generated-runbook-content p-3 mb-3" style={{
            backgroundColor: '#F9F9F9',
            border: '1px solid #CED4DA',
            borderRadius: '4px',
            maxHeight: '600px',
            overflowY: 'auto'
          }}>
            {/* Display runbook details - handle various response formats */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label fw-bold">Runbook Title:</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={
                      generatedRunbook.runbook_title || 
                      generatedRunbook['Runbook Title'] || 
                      generatedRunbook.title ||
                      'AI Generated Runbook'
                    } 
                    readOnly 
                  />
                </div>
              </div>
              <div className="col-md-3">
                <div className="mb-3">
                  <label className="form-label fw-bold">Tribe:</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={
                      generatedRunbook.tribe || 
                      generatedRunbook.Tribe || 
                      'OSFM'
                    } 
                    readOnly 
                  />
                </div>
              </div>
              <div className="col-md-3">
                <div className="mb-3">
                  <label className="form-label fw-bold">Solution:</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={
                      generatedRunbook.solution || 
                      generatedRunbook.Solution || 
                      'DMP'
                    } 
                    readOnly 
                  />
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Incident Description:</label>
              <textarea 
                className="form-control" 
                rows="3" 
                value={
                  generatedRunbook.incident_description || 
                  generatedRunbook['Incident Description'] ||
                  incidentDescription
                } 
                readOnly 
                style={{ backgroundColor: '#fff' }}
              />
            </div>

            {/* Display Tags if available */}
            {((generatedRunbook.tags && generatedRunbook.tags.length > 0) || 
              (generatedRunbook.Tags && generatedRunbook.Tags.length > 0)) && (
              <div className="mb-3">
                <label className="form-label fw-bold">Tags:</label>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {(generatedRunbook.tags || generatedRunbook.Tags || []).map((tag, index) => (
                    <span key={index} className="badge bg-primary fs-6 px-3 py-2">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Display Steps if available */}
            {generatedRunbook.steps && generatedRunbook.steps.length > 0 && (
              <div className="mb-3">
                <label className="form-label fw-bold">Resolution Steps ({generatedRunbook.steps.length} steps - will be converted to Modules):</label>
                <div className="list-group mt-2">
                  {generatedRunbook.steps.map((step, index) => (
                    <div key={index} className="list-group-item">
                      <div className="d-flex w-100 justify-content-between">
                        <h6 className="mb-1">
                          <span className="badge bg-success me-2">{index + 1}</span>
                          Step {index + 1}
                        </h6>
                      </div>
                      <p className="mb-1">{typeof step === 'string' ? step : step.description || JSON.stringify(step)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Display Modules if available (for saved runbooks) */}
            {generatedRunbook.Modules && generatedRunbook.Modules.length > 0 && (
              <div className="mb-3">
                <label className="form-label fw-bold">Modules ({generatedRunbook.Modules.length}):</label>
                <div className="table-responsive mt-2">
                  <table className="table table-striped table-bordered">
                    <thead className="table-dark">
                      <tr>
                        <th style={{ width: '150px' }}>Module ID</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generatedRunbook.Modules.map((module, index) => (
                        <tr key={index}>
                          <td><code>{module['Module ID'] || module.module_id || 'N/A'}</code></td>
                          <td>{module['Module Description'] || module.description || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Display other fields if present */}
            {generatedRunbook.product && (
              <div className="mb-3">
                <label className="form-label fw-bold">Product:</label>
                <input type="text" className="form-control" value={generatedRunbook.product} readOnly />
              </div>
            )}

            {generatedRunbook.service_offering && (
              <div className="mb-3">
                <label className="form-label fw-bold">Service Offering:</label>
                <input type="text" className="form-control" value={generatedRunbook.service_offering} readOnly />
              </div>
            )}

            {generatedRunbook.service_group && (
              <div className="mb-3">
                <label className="form-label fw-bold">Service Group:</label>
                <input type="text" className="form-control" value={generatedRunbook.service_group} readOnly />
              </div>
            )}

            {/* Display confidence score if available */}
            {generatedRunbook.confidence_score && (
              <div className="mb-3">
                <label className="form-label fw-bold">AI Confidence Score:</label>
                <div className="progress" style={{ height: '25px' }}>
                  <div 
                    className="progress-bar bg-success" 
                    role="progressbar" 
                    style={{ width: `${generatedRunbook.confidence_score * 100}%` }}
                    aria-valuenow={generatedRunbook.confidence_score * 100} 
                    aria-valuemin="0" 
                    aria-valuemax="100"
                  >
                    {Math.round(generatedRunbook.confidence_score * 100)}%
                  </div>
                </div>
              </div>
            )}

            {/* Display any additional metadata */}
            {(generatedRunbook.generated_by || generatedRunbook.Generated) && (
              <div className="mb-3">
                <span className="badge bg-info fs-6 px-3 py-2">
                  <i className="bi bi-robot me-2"></i>
                  Generated by: {generatedRunbook.generated_by || generatedRunbook.Generated || 'AI'}
                </span>
              </div>
            )}

            {/* Debug: Show raw JSON if structure is unexpected */}
            {!generatedRunbook.steps && !generatedRunbook.Modules && (
              <details className="mt-3">
                <summary className="btn btn-sm btn-outline-secondary">
                  <i className="bi bi-code-square me-2"></i>
                  Show Raw Response (Debug)
                </summary>
                <pre className="mt-2 p-3 bg-light border rounded" style={{ 
                  whiteSpace: 'pre-wrap', 
                  wordWrap: 'break-word',
                  fontSize: '12px',
                  maxHeight: '300px',
                  overflow: 'auto'
                }}>
                  {JSON.stringify(generatedRunbook, null, 2)}
                </pre>
              </details>
            )}

            <div className="alert alert-info mt-3" role="alert">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Note:</strong> This is a preview. Click "Save to Database" to create the runbook with all modules in your database.
            </div>
          </div>

          <div className="d-flex gap-2">
            <button 
              className="btn btn-success" 
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-save me-2"></i>
                  Save to Database
                </>
              )}
            </button>
            <button 
              className="btn btn-danger" 
              onClick={handleCancel}
              disabled={isSaving}
            >
              <i className="bi bi-x-circle me-2"></i>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Similar Runbooks Comparison */}
      {similarRunbooks.length > 0 && (
        <div className="card p-4 mb-4">
          <h2 className="mb-3">
            <i className="bi bi-diagram-3 me-2"></i>
            Similar Existing Runbooks
          </h2>
          
          <div className="alert alert-info" role="alert">
            <i className="bi bi-info-circle me-2"></i>
            Found {similarRunbooks.length} similar runbook{similarRunbooks.length !== 1 ? 's' : ''} in the database.
          </div>

          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Runbook ID</th>
                  <th>Title</th>
                  <th>Incident Description</th>
                  <th>Tribe</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {similarRunbooks.map((runbook, index) => (
                  <tr key={index}>
                    <td>{runbook['Runbook ID'] || runbook.runbook_id || 'N/A'}</td>
                    <td>{runbook['Runbook Title'] || runbook.runbook_title || 'N/A'}</td>
                    <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {runbook['Incident Description'] || runbook.incident_description || 'N/A'}
                    </td>
                    <td>{runbook.Tribe || runbook.tribe || 'N/A'}</td>
                    <td>
                      <button 
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => navigate(`/view_runbook/${runbook['Runbook ID'] || runbook.runbook_id}`)}
                      >
                        <i className="bi bi-eye me-1"></i>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!generatedRunbook && !isGenerating && (
        <div className="card p-5 text-center" style={{ backgroundColor: '#F8F9FA' }}>
          <i className="bi bi-robot" style={{ fontSize: '48px', color: '#007BFF' }}></i>
          <h3 className="mt-3 mb-2">Ready to Generate</h3>
          <p className="text-muted">
            Enter an incident description above and click "Generate Runbook" to create an AI-powered solution.
          </p>
        </div>
      )}
    </div>
  );
}

export default AIRunbookGenerator;
